import type { LucideIcon } from 'lucide-react';
import {
  FileText,
  Globe,
  Image as ImageIcon,
  Code2,
  Brain,
  Palette,
  Sparkles,
} from 'lucide-react';
import europeIcon from 'assets/images/europe-icon.svg';
import europeIconWhite from 'assets/images/europe-icon-white.svg';
import swissFlag from 'assets/images/swiss-flag.svg';
import {
  LLM_MODEL_REGIONS,
  type LlmModelRegion,
} from 'constant/llm-model';

export type ModelMetadataTranslateFn = (
  key: string,
  options?: Record<string, unknown>
) => string;

export interface ModelCapability {
  labelKey: string;
  icon: LucideIcon;
}

export interface ModelMetadata {
  provider: string;
  descriptionKey: string;
  capabilities: ModelCapability[];
  region?: LlmModelRegion;
}

export interface TranslatedModelMetadata {
  provider: string;
  description: string;
  capabilities: Array<{ label: string; icon: LucideIcon }>;
  region?: LlmModelRegion;
  hostingInfo?: string;
}

export const CAPABILITIES: Record<string, ModelCapability> = {
  documents: { labelKey: 'MODEL_CAPABILITY_DOCUMENTS', icon: FileText },
  webSearch: { labelKey: 'MODEL_CAPABILITY_WEB_SEARCH', icon: Globe },
  imageAnalysis: { labelKey: 'MODEL_CAPABILITY_IMAGE_ANALYSIS', icon: ImageIcon },
  dataAnalyst: { labelKey: 'MODEL_CAPABILITY_DATA_ANALYST', icon: Code2 },
  thinking: { labelKey: 'MODEL_CAPABILITY_THINKING', icon: Brain },
  canvas: { labelKey: 'MODEL_CAPABILITY_CANVAS', icon: Palette },
  generalChat: { labelKey: 'MODEL_CAPABILITY_GENERAL_CHAT', icon: Sparkles },
};

export const REGION_ICON_MAP: Record<LlmModelRegion, string | undefined> = {
  [LLM_MODEL_REGIONS.CH]: swissFlag,
  [LLM_MODEL_REGIONS.EU]: europeIcon,
  [LLM_MODEL_REGIONS.GLOBAL]: undefined,
};

export const REGION_ICON_WHITE_MAP: Record<LlmModelRegion, string | undefined> = {
  [LLM_MODEL_REGIONS.CH]: swissFlag,
  [LLM_MODEL_REGIONS.EU]: europeIconWhite,
  [LLM_MODEL_REGIONS.GLOBAL]: undefined,
};

export const REGION_HOSTING_INFO_KEYS: Record<LlmModelRegion, string> = {
  [LLM_MODEL_REGIONS.CH]: 'MODEL_HOSTING_CH',
  [LLM_MODEL_REGIONS.EU]: 'MODEL_HOSTING_EU',
  [LLM_MODEL_REGIONS.GLOBAL]: 'MODEL_HOSTING_GLOBAL',
};

// Static metadata keyed by model.id (which maps to `modelName` from the API).
// TODO: replace with values from the ModelConfiguration API once it returns
// provider / capabilities / region / hostingInfo.
export const MODEL_METADATA: Record<string, ModelMetadata> = {
  auto: {
    provider: 'SeliseAI',
    descriptionKey: 'MODEL_DESC_AUTO',
    capabilities: [
      CAPABILITIES.documents,
      CAPABILITIES.webSearch,
      CAPABILITIES.imageAnalysis,
      CAPABILITIES.dataAnalyst,
      CAPABILITIES.generalChat,
    ],
    region: LLM_MODEL_REGIONS.EU,
  },
  'gpt-4o': {
    provider: 'OpenAI',
    descriptionKey: 'MODEL_DESC_GPT_4O',
    capabilities: [
      CAPABILITIES.documents,
      CAPABILITIES.webSearch,
      CAPABILITIES.imageAnalysis,
      CAPABILITIES.dataAnalyst,
      CAPABILITIES.canvas,
      CAPABILITIES.generalChat,
    ],
    region: LLM_MODEL_REGIONS.EU,
  },
  'gpt-5': {
    provider: 'OpenAI',
    descriptionKey: 'MODEL_DESC_GPT_5',
    capabilities: [
      CAPABILITIES.documents,
      CAPABILITIES.webSearch,
      CAPABILITIES.imageAnalysis,
      CAPABILITIES.dataAnalyst,
      CAPABILITIES.thinking,
      CAPABILITIES.canvas,
    ],
    region: LLM_MODEL_REGIONS.EU,
  },
  'gpt-5-chat': {
    provider: 'OpenAI',
    descriptionKey: 'MODEL_DESC_GPT_5_CHAT',
    capabilities: [
      CAPABILITIES.documents,
      CAPABILITIES.webSearch,
      CAPABILITIES.imageAnalysis,
      CAPABILITIES.dataAnalyst,
      CAPABILITIES.canvas,
      CAPABILITIES.generalChat,
    ],
    region: LLM_MODEL_REGIONS.EU,
  },
  'gpt-5-nano': {
    provider: 'OpenAI',
    descriptionKey: 'MODEL_DESC_GPT_5_NANO',
    capabilities: [
      CAPABILITIES.documents,
      CAPABILITIES.imageAnalysis,
      CAPABILITIES.dataAnalyst,
      CAPABILITIES.generalChat,
    ],
    region: LLM_MODEL_REGIONS.EU,
  },
};

export const DEFAULT_MODEL_METADATA: ModelMetadata = {
  provider: 'SeliseAI',
  descriptionKey: 'MODEL_DESC_DEFAULT',
  capabilities: [
    CAPABILITIES.documents,
    CAPABILITIES.imageAnalysis,
    CAPABILITIES.generalChat,
  ],
  region: LLM_MODEL_REGIONS.EU,
};

export const getModelMetadata = (modelId?: string | null): ModelMetadata => {
  if (!modelId) {
    return DEFAULT_MODEL_METADATA;
  }
  return MODEL_METADATA[modelId] ?? DEFAULT_MODEL_METADATA;
};

const REGION_HOSTING_FALLBACKS: Record<LlmModelRegion, string> = {
  [LLM_MODEL_REGIONS.CH]: 'Hosted in Switzerland',
  [LLM_MODEL_REGIONS.EU]: 'Hosted in EU',
  [LLM_MODEL_REGIONS.GLOBAL]: 'Hosted globally',
};

export const getRegionIcon = (
  region?: LlmModelRegion,
  options?: { useWhiteIcon?: boolean }
): string | undefined => {
  if (!region) return undefined;
  const iconMap = options?.useWhiteIcon ? REGION_ICON_WHITE_MAP : REGION_ICON_MAP;
  return iconMap[region];
};

export const getRegionHostingInfo = (
  region?: LlmModelRegion,
  t?: ModelMetadataTranslateFn
): string | undefined => {
  if (!region) return undefined;
  const key = REGION_HOSTING_INFO_KEYS[region];
  if (t) {
    const translated = t(key);
    if (translated && translated !== key) {
      return translated;
    }
  }
  return REGION_HOSTING_FALLBACKS[region];
};

export const translateModelMetadata = (
  metadata: ModelMetadata,
  t: ModelMetadataTranslateFn,
  options?: { providerLabel?: string }
): TranslatedModelMetadata => {
  const providerLabel = options?.providerLabel ?? metadata.provider;
  const description =
    metadata.descriptionKey === 'MODEL_DESC_DEFAULT'
      ? t('MODEL_DESC_AVAILABLE_VIA_PROVIDER', { provider: providerLabel }) ||
        `Language model available through ${providerLabel}.`
      : t(metadata.descriptionKey) || metadata.descriptionKey;

  return {
    provider: metadata.provider,
    description,
    capabilities: metadata.capabilities.map((capability) => ({
      icon: capability.icon,
      label: t(capability.labelKey) || capability.labelKey,
    })),
    region: metadata.region,
    hostingInfo: metadata.region ? getRegionHostingInfo(metadata.region, t) : undefined,
  };
};

export const getTranslatedModelMetadata = (
  modelId: string | null | undefined,
  t: ModelMetadataTranslateFn,
  options?: { providerLabel?: string; region?: LlmModelRegion }
): TranslatedModelMetadata => {
  const metadata = getModelMetadata(modelId);
  const translated = translateModelMetadata(metadata, t, options);
  const region = options?.region ?? metadata.region;

  return {
    ...translated,
    region,
    hostingInfo: region ? getRegionHostingInfo(region, t) : undefined,
  };
};
